/**
 * Comportement commun des couches modales (fenetre, panneau lateral) : focus
 * capte a l'ouverture puis rendu au declencheur (ou a un repli s'il a
 * disparu), Tab qui boucle dans la couche, Echap, defilement de la page bloque.
 *
 * Les couches s'empilent (une confirmation ouverte depuis un panneau) : seule
 * la plus haute reagit au clavier, sinon Echap fermerait tout d'un coup.
 */
import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

/** Couches ouvertes, de la plus ancienne a celle du dessus. */
const pile: { refConteneur: RefObject<HTMLElement | null> }[] = [];
/** `body.style.overflow` avant l'ouverture de la premiere couche. */
let debordementInitial = '';

const SELECTEUR_FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]',
  '[contenteditable="true"]',
].join(', ');

/**
 * Present dans le DOM mais hors de portee de Tab : masque, inerte, ou contenu
 * d'un `<details>` replie (un menu deroulant en fin de panneau). Pris pour
 * premier ou dernier controle, il laisserait Tab sortir de la couche.
 */
const SELECTEUR_INATTEIGNABLE = '[hidden], [inert], details:not([open]) > :not(summary)';

export function elementsFocusables(conteneur: HTMLElement): HTMLElement[] {
  return Array.from(conteneur.querySelectorAll<HTMLElement>(SELECTEUR_FOCUSABLE)).filter(
    (element) =>
      // Un tabindex negatif retire aussi un bouton ou un lien de l'ordre de
      // tabulation (onglets inactifs d'une liste d'onglets).
      !(element.hasAttribute('tabindex') && element.tabIndex < 0) &&
      !element.closest(SELECTEUR_INATTEIGNABLE),
  );
}

/**
 * Donne le focus a `cible`, sinon au contenu principal (`main`) : cible
 * absente, retiree du document ou qui refuse le focus (masquee par une regle
 * d'affichage). Le focus ne retombe jamais sur la page entiere, point de
 * depart inutile au clavier.
 */
export function focaliserOuReplier(cible: HTMLElement | null | undefined): void {
  cible?.focus();
  if (!cible || document.activeElement !== cible) document.querySelector<HTMLElement>('main')?.focus();
}

/** Vrai tant qu'une couche est ouverte : les raccourcis globaux se taisent alors. */
export function coucheModaleOuverte(): boolean {
  return pile.length > 0;
}

/**
 * Faux quand une couche recouvre l'element : un menu laisse ouvert derriere
 * une fenetre ne doit lui prendre ni Echap ni le focus.
 */
export function auPremierPlan(element: Element): boolean {
  const dessus = pile[pile.length - 1];
  return dessus === undefined || (dessus.refConteneur.current?.contains(element) ?? false);
}

/**
 * Entree et sortie du focus d'une couche (fenetre, panneau, confirmation).
 *
 * Ne jamais utiliser `autoFocus` dans une couche : passer `refFocusInitial`.
 * L'attribut agit avant l'effet de la couche, qui prendrait alors le champ
 * pour le declencheur (focus perdu a la fermeture) et lui reprendrait le focus.
 */
export interface OptionsFocusCouche {
  /** Element focalise a l'ouverture (champ principal) ; a defaut, la couche elle-meme. */
  refFocusInitial?: RefObject<HTMLElement | null>;
  /**
   * Repli du focus a la fermeture quand le declencheur a quitte le document
   * (ligne retiree par l'action confirmee) ; a defaut, ou si la cible refuse
   * le focus, le contenu principal (`main`).
   */
  refFocusRetour?: RefObject<HTMLElement | null>;
}

export function useCoucheModale({
  ouverte,
  refConteneur,
  onEchap,
  refFocusInitial,
  refFocusRetour,
}: {
  ouverte: boolean;
  refConteneur: RefObject<HTMLElement | null>;
  /** Absent : Echap ne ferme pas (fenetre verrouillee). */
  onEchap?: () => void;
} & OptionsFocusCouche): void {
  // Lu au moment de la frappe : changer de gestionnaire ne relance pas
  // l'effet, qui rendrait puis reprendrait le focus.
  const refOnEchap = useRef(onEchap);
  refOnEchap.current = onEchap;
  // Meme raison : les cibles de focus sont lues a l'ouverture et a la fermeture.
  const refCibles = useRef({ refFocusInitial, refFocusRetour });
  refCibles.current = { refFocusInitial, refFocusRetour };

  useEffect(() => {
    if (!ouverte) return undefined;
    // Un objet par ouverture : son identite repere la couche dans la pile.
    const jeton = { refConteneur };
    // Bloque par la premiere couche, rendu par la derniere. Quand des couches
    // empilees disparaissent ensemble (changement de page), React nettoie le
    // parent avant l'enfant : une restauration couche par couche remettrait
    // `hidden` en dernier et la page ne defilerait plus.
    if (pile.length === 0) {
      debordementInitial = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    pile.push(jeton);
    // `body` n'est pas un declencheur (Safari ne donne pas le focus a un bouton
    // clique) : y revenir perdrait le focus, d'ou le repli.
    const actif = document.activeElement;
    const precedent = actif instanceof HTMLElement && actif !== document.body ? actif : null;
    (refCibles.current.refFocusInitial?.current ?? refConteneur.current)?.focus();

    const surTouche = (evenement: KeyboardEvent): void => {
      const conteneur = refConteneur.current;
      if (!conteneur || pile[pile.length - 1] !== jeton) return;

      if (evenement.key === 'Escape') {
        const fermer = refOnEchap.current;
        // Deja consommee par un element de la couche (un menu ouvert qui vient
        // de se refermer) : un Echap ne ferme qu'une chose a la fois.
        if (fermer && !evenement.defaultPrevented) {
          evenement.preventDefault();
          fermer();
        }
        return;
      }
      if (evenement.key !== 'Tab') return;

      const focusables = elementsFocusables(conteneur);
      const premier = focusables[0];
      const dernier = focusables[focusables.length - 1];
      if (!premier || !dernier) {
        evenement.preventDefault();
        conteneur.focus();
        return;
      }
      const actif = document.activeElement;
      if (!conteneur.contains(actif)) {
        evenement.preventDefault();
        premier.focus();
      } else if (evenement.shiftKey && (actif === premier || actif === conteneur)) {
        evenement.preventDefault();
        dernier.focus();
      } else if (!evenement.shiftKey && actif === dernier) {
        evenement.preventDefault();
        premier.focus();
      }
    };

    document.addEventListener('keydown', surTouche);
    return () => {
      document.removeEventListener('keydown', surTouche);
      const position = pile.indexOf(jeton);
      if (position !== -1) pile.splice(position, 1);
      if (pile.length === 0) document.body.style.overflow = debordementInitial;
      // Un declencheur retire du document ne peut plus recevoir le focus ; un
      // declencheur masque (bouton de menu `lg:hidden` quand le tiroir se ferme
      // au passage en grand ecran) le refuse : repli dans les deux cas.
      focaliserOuReplier(precedent?.isConnected ? precedent : refCibles.current.refFocusRetour?.current);
    };
  }, [ouverte, refConteneur]);
}
